import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import frontendLogger from "../utils/frontendLogger.js";
import { API_BASE_URL } from "../app_config.js";
import "../pages/css/App.css";
import "../pages/css/ManageUser.css";
import "../pages/css/InfoMessages.css";

const getAuthToken = () => localStorage.getItem("token");
const ITEMS_PER_PAGE = 5;

export default function ManageUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const handleBackToStart = () => {
    navigate("/main");
  };

  // Fetch all users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/userManagement/users`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (!response.ok) {
          throw new Error("Fehler beim Abrufen der Benutzer");
        }

        const data = await response.json();
        setUsers(data.users || []);
        frontendLogger.info("Users fetched:", data.users);
      } catch (error) {
        frontendLogger.error("Error fetching users:", error);
        setMessage("Fehler beim Laden der Benutzer");
        setMessageType("error");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Toggle user selection
  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  // Select all users on current page
  const handleSelectAll = () => {
    if (selectedUsers.size === paginatedUsers.length) {
      setSelectedUsers(new Set());
    } else {
      const pageUserIds = paginatedUsers.map((u) => u.id);
      const newSelected = new Set(selectedUsers);
      pageUserIds.forEach((id) => newSelected.add(id));
      setSelectedUsers(newSelected);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(users.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedUsers = users.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Block selected users
  const handleBlockUsers = async () => {
    if (selectedUsers.size === 0) {
      setMessage("Bitte wählen Sie mindestens einen Benutzer aus");
      setMessageType("warning");
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/userManagement/block-users`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userIds: Array.from(selectedUsers),
            blocked: true,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Fehler beim Sperren der Benutzer");
      }

      const data = await response.json();
      setMessage(data.message);
      setMessageType("success");

      // Refresh user list
      const usersResponse = await fetch(
        `${API_BASE_URL}/api/userManagement/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const usersData = await usersResponse.json();
      setUsers(usersData.users || []);
      setSelectedUsers(new Set());
      setCurrentPage(1);

      frontendLogger.info("Users blocked successfully");
    } catch (error) {
      frontendLogger.error("Error blocking users:", error);
      setMessage(error.message || "Fehler beim Sperren der Benutzer");
      setMessageType("error");
    }
  };

  // Unblock selected users
  const handleUnblockUsers = async () => {
    if (selectedUsers.size === 0) {
      setMessage("Bitte wählen Sie mindestens einen Benutzer aus");
      setMessageType("warning");
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/userManagement/block-users`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userIds: Array.from(selectedUsers),
            blocked: false,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Fehler beim Entsperren der Benutzer");
      }

      const data = await response.json();
      setMessage(data.message);
      setMessageType("success");

      // Refresh user list
      const usersResponse = await fetch(
        `${API_BASE_URL}/api/userManagement/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const usersData = await usersResponse.json();
      setUsers(usersData.users || []);
      setSelectedUsers(new Set());
      setCurrentPage(1);

      frontendLogger.info("Users unblocked successfully");
    } catch (error) {
      frontendLogger.error("Error unblocking users:", error);
      setMessage(error.message || "Fehler beim Entsperren der Benutzer");
      setMessageType("error");
    }
  };

  if (loading) {
    return (
      <div className="page-layout">
        <div className="content-container">
          <p>Benutzerliste wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <div className="menu-container">
        <div className="title-box">
          <p>DRAM - Benutzerverwaltung</p>
        </div>
        <div className="menu-box">
          <h1>Benutzer verwalten</h1>
        </div>
        <nav className="nav">
          <button className="nav-button" onClick={handleBackToStart}>
            Startseite
          </button>

        </nav>
      </div>

      <div className="content-container">
        {message && (
          <div
            className={`info-message ${messageType === "error" ? "error" : messageType === "warning" ? "warning" : "success"}`}
          >
            {message}
          </div>
        )}

        <div className="user-list-header">
          <h2>Benutzerliste</h2>
          <p>Insgesamt: {users.length} Benutzer</p>
        </div>

        <div className="card-table-container">
          <table className="card-table">
            <thead>
              <tr>
                <th style={{ width: "50px" }}>
                  <input
                    type="checkbox"
                    checked={
                      paginatedUsers.length > 0 &&
                      paginatedUsers.every((u) => selectedUsers.has(u.id))
                    }
                    onChange={handleSelectAll}
                    title="Alle auf dieser Seite auswählen"
                  />
                </th>
                <th>Benutzername</th>
                <th>Rolle</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="3" style={{ textAlign: "center" }}>
                    Keine Benutzer gefunden
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                      />
                    </td>
                    <td>{user.username}</td>
                    <td>
                      <span
                        className={`role-badge ${
                          user.role === "blocked"
                            ? "blocked"
                            : user.role === "admin"
                              ? "admin"
                              : "user"
                        }`}
                      >
                        {user.role === "blocked"
                          ? "Gesperrt"
                          : user.role === "admin"
                            ? "Admin"
                            : "Benutzer"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i> Vorherige
              </button>
              <span className="pagination-info">
                Seite {currentPage} von {totalPages}
              </span>
              <button
                className="pagination-button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Nächste <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </div>

        {selectedUsers.size > 0 && (
          <div className="action-panel">
            <p>{selectedUsers.size} Benutzer ausgewählt</p>
            <div className="action-buttons">
              <button onClick={handleBlockUsers} className="btn-block">
                Benutzer sperren
              </button>
              <button onClick={handleUnblockUsers} className="btn-unblock">
                Benutzer entsperren
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
